import { Images } from "assets/images";
import CustomField from "components/form/CustomField";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getVerifyInviteValidationSchema } from "utils/validations";
import { Eye, EyeOff, AlertTriangle, CheckCircle2 } from "lucide-react";
import { showAlert } from "store/slices/alertSlice";
import { setPassword, verifyInvite } from "store/slices/authSlice";
import { Spinner, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const initialValues = {
  password: "",
  confirmPassword: "",
};

const VerifyInvite = () => {
  const { t } = useTranslation();
  const token = useQueryParam("token");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, isLoading } = useSelector((state) => state.Auth);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const [showPosDialog, setShowPosDialog] = useState(true);

  const { logo } = Images || {};

  useEffect(() => {
    if (!token) return;

    dispatch(verifyInvite(token))
      .unwrap()
      .then((data) => {
        const { statusCode, success } = data || {};
        if (statusCode === 200 && success) {
          setIsVerified(true);
        }
      })
      .catch((error) => {
        setError(error?.message || error);
        dispatch(
          showAlert({
            text: error?.message || error || t("Something went wrong"),
            type: "error",
          })
        );

        setIsVerified(false);
      });
  }, [token, dispatch]);

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      token,
    };
    handleSubmitForm({
      dispatch,
      action: setPassword,
      payload,
      onSuccess: (data) => {
        const accountType = data?.accountType;
        if (accountType && accountType.toLowerCase() === "pos") {
          setShowPosDialog(true);
        } else {
          navigate("/dashboard");
        }
      },
    });
  };

  return (
    <div className="page-content login_page d-flex justify-content-center align-items-center">
      {loading ? (
        <Spinner />
      ) : (
        error && (
          <div
            className="login-box p-4 shadow-sm rounded bg-white text-center"
            style={{ maxWidth: 420 }}
          >
            <AlertTriangle size={48} className="text-danger mb-3" />
            <h5 className="mb-2">{t("Something went wrong")}</h5>
            <p className="text-muted mb-4">{error}</p>
            <button
              type="button"
              className="main_btn w-100"
              onClick={() => navigate("/login")}
            >
              {t("Back to Login")}
            </button>
          </div>
        )
      )}
      {isVerified && (
        <div
          className="login-box p-4 shadow-sm rounded bg-white"
        >
          {/* Logo */}
          <div className="text-center mb-4">
            <img src={logo} alt={t("Logo")} width={100} height={55} />
          </div>

          <h4 className="text-center mb-3">{t("Set New Password")}</h4>

          <Formik
            initialValues={initialValues}
            validationSchema={getVerifyInviteValidationSchema(t)}
            onSubmit={handleSubmit}
          >
            {({}) => (
              <Form>
                <div className="form_field position-relative">
                  <CustomField
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label={t("Password")}
                    placeholder={t("Enter Password")}
                  />
                  <span
                    className="password_eye"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>{" "}
                <div className="form_field position-relative">
                  <CustomField
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    label={t("Confirm Password")}
                    placeholder={t("Enter Confirm Password")}
                  />
                  <span
                    className="password_eye"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </span>
                </div>{" "}
                <button
                  type="submit"
                  className="main_btn w-100 mt-3"
                  disabled={isLoading}
                >
                  {t("Set Password")}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <Modal
        show={showPosDialog}
        onHide={() => navigate("/login")}
        centered
        contentClassName="border-0 pos-dialog"
      >
        <Modal.Body
          className="text-center p-5"
        >
          <div
            className="d-inline-flex align-items-center justify-content-center mb-4 circle_icon_wrapper"
          >
            <CheckCircle2 size={48} />
          </div>
          <h3 className="fw-bold mb-2">{t("You're All Set!")}</h3>
          <p className="mb-4" style={{ opacity: 0.9 }}>
            {t("You can now login to your POS app")}
          </p>
          <button
            type="button"
            className="main_btn w-100 fw-semibold"
            onClick={() => navigate("/login")}
          >
            {t("Got it")}
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default VerifyInvite;
